import { useCallback } from 'react'
import { HTMLContainer, Rectangle2d, ShapeUtil, TLBaseShape, resizeBox, useEditor } from 'tldraw'
import { LiveSeriesCard } from '@/components/live/LiveSeriesCard'
import { shapeNodeToSvgImage } from '../utils/shapeNodeToSvgImage'

/**
 * A live feed card: the recent readings of a time-series document. It is deliberately not a
 * document card, so it carries no `meta.document_id`: the board keys, repositions and deletes
 * document cards by that field, and this one lives only in the saved tldraw snapshot.
 */
export type LiveSeriesShape = TLBaseShape<
  'LiveSeries',
  {
    w: number
    h: number
    documentId?: string
    /** Tag ids chosen in the card's picker; unset until the user picks (the server chooses). */
    tags?: string[]
    /** `15m` / `1h` / `1d` or an ISO 8601 duration; unset means the server default of 1 hour. */
    window?: string
    /** The document's name when the card was inserted. */
    name?: string
  }
>

export const LIVE_SERIES_WIDTH = 520
export const LIVE_SERIES_HEIGHT = 380
export const LIVE_SERIES_MIN_WIDTH = 320
export const LIVE_SERIES_MIN_HEIGHT = 260

export const LiveSeriesShapeComponent = ({ shape }: { shape: LiveSeriesShape }) => {
  const editor = useEditor()
  const { documentId, tags, window, name } = shape.props

  const handleTagsChange = useCallback(
    (next: string[]) => {
      editor.updateShape<LiveSeriesShape>({
        id: shape.id,
        type: 'LiveSeries',
        props: { tags: next },
      })
    },
    [editor, shape.id]
  )

  if (!documentId) return null
  return (
    <HTMLContainer style={{ width: shape.props.w, height: shape.props.h, pointerEvents: 'all' }}>
      <LiveSeriesCard
        documentId={documentId}
        tags={tags}
        window={window}
        title={name}
        onTagsChange={handleTagsChange}
      />
    </HTMLContainer>
  )
}

export class LiveSeriesShapeUtil extends ShapeUtil<LiveSeriesShape> {
  static override type = 'LiveSeries' as const

  getDefaultProps(): LiveSeriesShape['props'] {
    return { w: LIVE_SERIES_WIDTH, h: LIVE_SERIES_HEIGHT }
  }

  getGeometry(shape: LiveSeriesShape) {
    return new Rectangle2d({ width: shape.props.w, height: shape.props.h, isFilled: true })
  }

  component(shape: LiveSeriesShape) {
    if (!shape) return null
    return <LiveSeriesShapeComponent shape={shape} />
  }

  indicator(shape: LiveSeriesShape) {
    return <rect width={shape.props.w} height={shape.props.h} rx={12} />
  }

  override canResize() {
    return true
  }

  override onResize(shape: LiveSeriesShape, info: Parameters<typeof resizeBox>[1]) {
    return resizeBox(shape, info, { minWidth: LIVE_SERIES_MIN_WIDTH, minHeight: LIVE_SERIES_MIN_HEIGHT })
  }

  // FS-482: rasterize the live DOM for off-DOM exports. See shapeNodeToSvgImage.
  override toSvg(shape: LiveSeriesShape) {
    return shapeNodeToSvgImage(shape.id, shape.props.w, shape.props.h)
  }
}
